import { useCallback, useEffect, useRef, useState } from 'react';

/** 새 버전 체크 폴링 간격 (ms) */
const POLL_INTERVAL = 5 * 60 * 1000; // 5분

interface VersionJson {
  version: string;
  /** ISO build/deploy timestamp emitted into version.json at build time. */
  buildTime?: string;
}

/**
 * 배포된 버전과 현재 실행 중인 버전을 비교해 업데이트 여부를 반환한다.
 *
 * - 빌드 시 Vite가 주입한 __APP_BUILD_HASH__ 를 초기 버전으로 사용
 * - 5분마다 /version.json 을 폴링해 해시가 다르면 updateAvailable = true
 * - dev 환경에서는 동작하지 않음
 */
export function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  // Build/deploy time of the newly-deployed version (from the polled version.json).
  const [deployedBuildTime, setDeployedBuildTime] = useState<string | null>(null);
  const currentHash = useRef<string>(typeof __APP_BUILD_HASH__ !== 'undefined' ? __APP_BUILD_HASH__ : '');

  const checkVersion = useCallback(async () => {
    try {
      const res = await fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;
      const data = (await res.json()) as VersionJson;
      if (currentHash.current && data.version && data.version !== currentHash.current) {
        setUpdateAvailable(true);
        setDeployedBuildTime(data.buildTime ?? null);
      }
    } catch {
      // 네트워크 오류 무시
    }
  }, []);

  useEffect(() => {
    if (import.meta.env.DEV) return;

    const timer = setInterval(() => void checkVersion(), POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [checkVersion]);

  const dismiss = useCallback(() => setUpdateAvailable(false), []);
  const triggerForTest = useCallback(() => {
    setUpdateAvailable(true);
    // Preview the banner with a representative build time (real deploys read it from version.json).
    setDeployedBuildTime(new Date().toISOString());
  }, []);

  return { updateAvailable, deployedBuildTime, dismiss, triggerForTest };
}
